
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AutomationRule } from '../types/automationTypes';
import { automationRuleSchema, AutomationRuleFormData } from '../schemas/automationRuleSchema';
import { useAutomationRuleValidation } from './useAutomationRuleValidation';

export const useAutomationRuleForm = (
  rule?: AutomationRule,
  onSubmit?: (data: AutomationRuleFormData) => void
) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { validateRule, validationResult, isValidating } = useAutomationRuleValidation(rule);

  const form = useForm<AutomationRuleFormData>({
    resolver: zodResolver(automationRuleSchema),
    defaultValues: rule ? {
      name: rule.name,
      description: rule.description,
      enabled: rule.enabled,
      trigger: rule.trigger,
      actions: rule.actions,
      priority: rule.priority,
    } : {
      name: '',
      description: '',
      enabled: true,
      trigger: {
        type: 'candidate_status_change',
        conditions: {},
      },
      actions: [{
        type: 'create_task',
        parameters: {
          title: '',
          priority: 'medium',
          dueInDays: 1,
        },
      }],
      priority: 'medium',
    },
  });

  const handleSubmit = async (data: AutomationRuleFormData) => {
    // Validate before submission
    const validation = await validateRule(data);
    
    if (!validation.isValid) {
      console.error('Validation failed:', validation.errors);
      return;
    }

    onSubmit?.(data);
    setIsDialogOpen(false);
    form.reset();
  };

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => {
    setIsDialogOpen(false);
    form.reset();
  };

  return {
    form,
    isDialogOpen,
    openDialog,
    closeDialog,
    handleSubmit,
    validationResult,
    isValidating,
    validateRule,
  };
};

export type { AutomationRuleFormData };
